import ReactGA4 from "react-ga4";

const GA_MEASUREMENT_ID = "G-PXNC5S04ZS";
const isProd = process.env.REACT_APP_ENV === "prod";

/**
 * Centralized Google Analytics 4 (GA4) Service
 * - Production: Dispatches live events to GA4 (G-PXNC5S04ZS)
 */
class AnalyticsService {
  constructor() {
    this.initialized = false;
    this.lastPagePath = null;
    this.lastEventKey = null;
    this.lastEventTime = 0;
  }

  /**
   * Initialize GA4 (run once on app startup)
   */
  init() {
    if (this.initialized) return;

    if (isProd) {
      ReactGA4.initialize(GA_MEASUREMENT_ID);
    }
    this.initialized = true;
  }

  /**
   * Synchronize GA4 with MongoDB User ID (userRefId)
   * Ensures GA4 sessions map 1-to-1 with MongoDB database records
   * @param {string} userId - MongoDB userRefId
   */
  setUserId(userId) {
    if (!userId) return;

    if (isProd) {
      ReactGA4.set({ user_id: userId });
    }
  }

  /**
   * Clear GA4 User ID on user logout
   */
  clearUserId() {
    this.lastPagePath = null;
    if (isProd) {
      ReactGA4.set({ user_id: null });
    }
  }

  /**
   * Track SPA route changes (automatically called by RouteTracker)
   * Deduplicates identical consecutive pageviews (e.g. from React 18 StrictMode)
   * @param {string} path - e.g. "/skills-calculation"
   * @param {string} title - Human readable page title
   */
  pageView(path, title) {
    if (this.lastPagePath === path) return;
    this.lastPagePath = path;

    const pageTitle = title || document.title;
    if (isProd) {
      ReactGA4.send({
        hitType: "pageview",
        page: path,
        title: pageTitle,
      });
    }
  }

  /**
   * Generic GA4 Event dispatcher
   * Deduplicates rapid identical event dispatches (within 300ms)
   * @param {string} name - Event name (snake_case)
   * @param {object} params - Event parameters
   */
  trackEvent(name, params = {}) {
    const eventKey = `${name}_${JSON.stringify(params)}`;
    const now = Date.now();
    if (this.lastEventKey === eventKey && now - this.lastEventTime < 300) {
      return;
    }
    this.lastEventKey = eventKey;
    this.lastEventTime = now;

    if (isProd) {
      ReactGA4.event(name, params);
    }
  }

  /**
   * Track Call-To-Action button clicks
   * @param {string} ctaName - Name of the CTA (e.g. "calculate_salary")
   * @param {string} location - Location on screen (e.g. "hero_section", "header_nav")
   */
  trackClickCTA(ctaName, location = "main") {
    this.trackEvent("click_cta", {
      cta_name: ctaName,
      location: location,
    });
  }

  /**
   * Track User Login Completed
   * @param {string} method - "email_otp", "google", "linkedin"
   */
  trackLogin(method = "email_otp") {
    this.trackEvent("login_completed", { method });
  }

  /**
   * Track User Login Attempt (Pre-login action)
   * @param {string} method - "email_otp", "google", "linkedin"
   * @param {string} location - "signin_page"
   */
  trackLoginAttempt(method = "email_otp", location = "signin_page") {
    this.trackEvent("login_attempt", { method, location });
  }

  /**
   * Track Login Failure
   * @param {string} method - "email_otp", "google", "linkedin"
   * @param {string} errorReason - Normalized reason: "invalid_otp", "max_attempts_exceeded", "oauth_failed", "auth_service_error"
   */
  trackLoginFailure(method = "email_otp", errorReason = "unknown_error") {
    this.trackEvent("login_failed", {
      method,
      error_reason: errorReason,
    });
  }

  /**
   * Track Calculator Step Proceed (User completes a step and moves forward)
   * @param {number} fromStepNumber - 1-based step number being completed
   * @param {string} fromStepName - Name of the step being completed
   * @param {number} toStepNumber - 1-based step number being advanced to
   */
  trackCalculatorProceed(fromStepNumber, fromStepName, toStepNumber) {
    this.trackEvent("calculator_step_proceed", {
      step_number: fromStepNumber,
      step_name: fromStepName,
      from_step_number: fromStepNumber,
      from_step_name: fromStepName,
      to_step_number: toStepNumber,
    });
  }

  /**
   * Track Calculator Step Back (User navigates backward)
   * @param {number} fromStepNumber - 1-based step number where back was clicked
   * @param {string} fromStepName - Name of the step where back was clicked
   * @param {number} toStepNumber - 1-based step number returned to
   */
  trackCalculatorBack(fromStepNumber, fromStepName, toStepNumber) {
    this.trackEvent("calculator_step_back", {
      step_number: fromStepNumber,
      step_name: fromStepName,
      from_step_number: fromStepNumber,
      from_step_name: fromStepName,
      to_step_number: toStepNumber,
    });
  }

  /**
   * Track Key Conversion Events
   * @param {string} conversionName - Name of the conversion (e.g. "calculator_complete")
   * @param {object} params - Additional parameters
   */
  trackConversion(conversionName, params = {}) {
    this.trackEvent(conversionName, params);
  }
}

const analytics = new AnalyticsService();
export default analytics;
